import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgbCalendar, NgbDate, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

export interface DateRangeOptions {
	minDate?: Date | null;
	maxDaysFromStart?: number;
	displayMonths?: number;
	autoClose?: boolean | 'inside' | 'outside';
	navigation?: 'select' | 'arrows';
	invisible?: boolean;
	startDateControl?: FormControl<Date | null>;
	endDateControl?: FormControl<Date | null>;
}
@Component({
	selector: 'maj-date-range-picker',
	imports: [NgbInputDatepicker],
	templateUrl: './date-range-picker.component.html',
})

export class DateRangePickerComponent implements OnInit, OnDestroy {
	@Input({required: true})
	options: DateRangeOptions | undefined;

	@Output()
	rangeChange = new EventEmitter<{ startDate: Date | null, endDate: Date | null }>();

	@ViewChild('dp')
	datePicker?: NgbInputDatepicker;

	calendar = inject(NgbCalendar);

	fromDate: NgbDate | null = null;
	toDate: NgbDate | null = null;
	today: NgbDate = this.calendar.getToday();
	readonly defaultMin = new NgbDate(1900, 1, 1);
	readonly defaultMax = new NgbDate(2099, 12, 31);
	private subscriptions = new Subscription();

	ngOnInit(): void {
		const startControl = this.options?.startDateControl;
		const endControl = this.options?.endDateControl;
		const startVal: Date | null = startControl?.value ?? null;
		const endVal: Date | null = endControl?.value ?? null;
		this.fromDate = this.toNgbDate(startVal);
		this.toDate = this.toNgbDate(endVal);

		if (startControl) {
			this.subscriptions.add(
				startControl.valueChanges.subscribe((val: Date | null) => {
					this.fromDate = this.toNgbDate(val);
				}),
			);
		}
		if (endControl) {
			this.subscriptions.add(
				endControl.valueChanges.subscribe((val: Date | null) => {
					this.toDate = this.toNgbDate(val);
				}),
			);
		}
	}

	private toNgbDate(date?: Date | null): NgbDate | null {
		return date ? new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate()) : null;
	}

	get minDateForPicker(): NgbDate | undefined {
		const minDate = this.options?.minDate;
		if (!minDate) return undefined;
		return new NgbDate(minDate.getFullYear(), minDate.getMonth() + 1, minDate.getDate());
	}

	get maxDateForPicker(): NgbDate | undefined {
		const base = this.fromDate;
		const days = this.options?.maxDaysFromStart;
		if (!base || !days) return undefined;
		return this.calendar.getNext(base, 'd', days);
	}

	onDateSelection(date: NgbDate) {
		if (!this.fromDate && !this.toDate) {
			this.fromDate = date;
		} else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
			this.toDate = date;
			this.datePicker?.close();
		} else {
			this.toDate = null;
			this.fromDate = date;
		}
		const startControl = this.options?.startDateControl;
		const endControl = this.options?.endDateControl;
		const startDateObj = this.toDateObj(this.fromDate);
		const endDateObj = this.toDateObj(this.toDate);
		if (startControl) {
			startControl.setValue(startDateObj, { emitEvent: false });
		}
		if (endControl) {
			endControl.setValue(endDateObj, { emitEvent: false });
		}
		this.emitRange();
	}

	open() {
		this.datePicker?.open();
	}

	close() {
		this.datePicker?.close();
	}

	toDateObj(date?: NgbDate | null): Date | null {
		if (!date) return null;
		return new Date(date.year, date.month - 1, date.day);
	}

	private emitRange() {
		const startDate = this.toDateObj(this.fromDate);
		const endDate = this.toDateObj(this.toDate);
		this.rangeChange.emit({ startDate, endDate });
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}
}
